import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

// URL pattern: /interview/select-slot/:tenantB64/:interviewHistoryId
// e.g.       : /interview/select-slot/ZGVmYXVsdFRlbmFudA==/73

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

// Decode base64 tenant — same helper as JobApplyPage
function decodeTenantId(b64) {
  try {
    const normalized = b64
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    return atob(normalized);
  } catch {
    return null;
  }
}

const fmtTime = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const fmtDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const groupByDate = (slots) => {
  const map = {};
  slots.forEach((s) => {
    const key = new Date(s.slotStartDateTime).toDateString();
    if (!map[key]) map[key] = [];
    map[key].push(s);
  });
  return Object.entries(map).sort(([a], [b]) => new Date(a) - new Date(b));
};

export default function InterviewSlotBooking() {
  const { tenantB64, interviewHistoryId } = useParams();
  const tenantId = decodeTenantId(tenantB64);

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [closingDate, setClosingDate] = useState(null);

  useEffect(() => {
    if (!interviewHistoryId || !tenantId) {
      setError("Invalid booking link.");
      setLoading(false);
      return;
    }
    fetch(`${API_BASE}/api/public/interview/${interviewHistoryId}/slots`, {
      headers: { "X-Tenantid": tenantId },
    })
      .then(async (r) => {
        const d = await r.json().catch(() => null);
        if (!r.ok) {
          throw new Error(d?.message || "Failed to load slots.");
        }
        return d;
      })
      .then((d) => {
        if (d.data) {
          setSlots(d.data);
          if (d.data[0]?.expectedClosingDate)
            setClosingDate(d.data[0].expectedClosingDate);
        } else setError(d.message || "Could not load slots.");
      })
      .catch((err) =>
        setError(err.message || "Failed to fetch available slots."),
      )
      .finally(() => setLoading(false));
  }, [interviewHistoryId, tenantId]);

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/public/interview/${interviewHistoryId}/confirm-slot`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "X-Tenantid": tenantId,
          },
          body: JSON.stringify({ slotId: selected.id }),
        },
      );
      const d = await res.json();
      if (res.ok) {
        setConfirmed(true);
        setConfirmMsg(d.data || "Your slot has been confirmed!");
      } else {
        setError(d.message || "Failed to confirm slot. Please try again.");
      }
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const allowedStatuses = ["AVAILABLE", "BLOCKED", "RESCHEDULED"];

  const availableSlots = slots.filter((s) =>
    allowedStatuses.includes(s.status),
  );
  const grouped = groupByDate(availableSlots);

  // ── Invalid link ──────────────────────────────────────────────────────────
  if (!tenantId && !loading) {
    return (
      <div style={styles.page}>
        <style>{cssRaw}</style>
        <div style={styles.card}>
          <div style={styles.emptyBox}>
            <span style={{ fontSize: 40 }}>🔗</span>
            <p style={styles.emptyTitle}>Invalid Link</p>
            <p style={styles.emptySub}>
              This booking link appears to be invalid or expired. Please contact
              the HR team.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Confirmed ─────────────────────────────────────────────────────────────
  if (confirmed) {
    return (
      <div style={styles.page}>
        <style>{cssRaw}</style>
        <div style={styles.topbar}>
          <TopbarContent />
        </div>
        <div style={styles.card}>
          <div style={styles.successRing}>
            <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
              <circle cx="28" cy="28" r="28" fill="#dcfce7" />
              <path
                d="M16 28l9 9 15-15"
                stroke="#16a34a"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 style={styles.successTitle}>Slot Confirmed!</h2>
          <p style={styles.successSub}>{confirmMsg}</p>
          {selected && (
            <div style={styles.slotSummary}>
              <div style={styles.slotSummaryRow}>
                <span style={styles.slotSummaryLabel}>Date</span>
                <span style={styles.slotSummaryVal}>
                  {fmtDate(selected.slotStartDateTime)}
                </span>
              </div>
              <div style={styles.slotSummaryRow}>
                <span style={styles.slotSummaryLabel}>Time</span>
                <span style={styles.slotSummaryVal}>
                  {fmtTime(selected.slotStartDateTime)} –{" "}
                  {fmtTime(selected.slotEndDateTime)}
                </span>
              </div>
              {selected.interviewerName && (
                <div style={styles.slotSummaryRow}>
                  <span style={styles.slotSummaryLabel}>Interviewer</span>
                  <span style={styles.slotSummaryVal}>
                    {selected.interviewerName}
                  </span>
                </div>
              )}
            </div>
          )}
          <p style={styles.confirmNote}>
            Please keep this time free. You may receive a calendar invite or
            further communication shortly.
          </p>
        </div>
        <div style={styles.footer}>Powered by your HR platform</div>
      </div>
    );
  }

  // ── Main page ─────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <style>{cssRaw}</style>

      <div style={styles.topbar}>
        <TopbarContent />
      </div>

      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h1 style={styles.title}>Pick Your Interview Slot</h1>
          <p style={styles.subtitle}>
            Choose a time that works for you. Slots are first-come,
            first-served.
          </p>

          {closingDate && (
            <div style={styles.closingBadge}>
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                style={{ flexShrink: 0 }}
              >
                <rect
                  x="1"
                  y="2"
                  width="11"
                  height="10"
                  rx="2"
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth="1.2"
                />
                <path
                  d="M1 5h11"
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth="1.2"
                />
                <path
                  d="M4 1v2M9 1v2"
                  stroke="rgba(255,255,255,0.8)"
                  strokeWidth="1.2"
                  strokeLinecap="round"
                />
              </svg>
              <span>
                Please book by&nbsp;<strong>{fmtDate(closingDate)}</strong>
              </span>
            </div>
          )}
        </div>

        {loading && (
          <div style={styles.center}>
            <div className="isb-spin" />
            <p style={{ color: "#6b7280", marginTop: 14, fontSize: 14 }}>
              Loading available slots…
            </p>
          </div>
        )}

        {error && !loading && (
          <div style={styles.errorBox}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <p style={{ margin: 0, fontSize: 14 }}>{error}</p>
          </div>
        )}

        {!loading && !error && availableSlots.length === 0 && (
          <div style={styles.emptyBox}>
            <span style={{ fontSize: 40 }}>📅</span>
            <p style={styles.emptyTitle}>No slots available</p>
            <p style={styles.emptySub}>
              There are currently no open interview slots. Please contact the HR
              team for assistance.
            </p>
          </div>
        )}

        {!loading && !error && grouped.length > 0 && (
          <>
            <div style={styles.groupsWrap}>
              {grouped.map(([dateKey, daySlots]) => (
                <div key={dateKey} style={styles.dateGroup}>
                  <div style={styles.dateLabel}>
                    <span style={styles.dateDot} />
                    {fmtDate(daySlots[0].slotStartDateTime)}
                  </div>
                  <div style={styles.slotsGrid}>
                    {daySlots.map((slot) => {
                      const isSelected = selected?.id === slot.id;
                      return (
                        <button
                          key={slot.id}
                          className={`isb-slot${isSelected ? " isb-slot--selected" : ""}`}
                          style={{
                            ...styles.slotBtn,
                            ...(isSelected ? styles.slotBtnSelected : {}),
                          }}
                          onClick={() => setSelected(isSelected ? null : slot)}
                        >
                          <span style={styles.slotTime}>
                            {fmtTime(slot.slotStartDateTime)}
                          </span>
                          <span style={styles.slotSep}>–</span>
                          <span style={styles.slotTime}>
                            {fmtTime(slot.slotEndDateTime)}
                          </span>
                          {slot.interviewerName && (
                            <span style={styles.slotInterviewer}>
                              👤 {slot.interviewerName}
                            </span>
                          )}
                          {isSelected && (
                            <span style={styles.slotCheck}>✓</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {selected && (
              <div style={styles.selectedBanner}>
                <div>
                  <div style={styles.selectedBannerTitle}>Selected Slot</div>
                  <div style={styles.selectedBannerVal}>
                    {fmtDate(selected.slotStartDateTime)},{" "}
                    {fmtTime(selected.slotStartDateTime)} –{" "}
                    {fmtTime(selected.slotEndDateTime)}
                    {selected.interviewerName && (
                      <> &nbsp;·&nbsp; {selected.interviewerName}</>
                    )}
                  </div>
                </div>
                <button
                  className="isb-confirm-btn"
                  style={{
                    ...styles.confirmBtn,
                    ...(confirming ? styles.confirmBtnLoading : {}),
                  }}
                  onClick={handleConfirm}
                  disabled={confirming}
                >
                  {confirming ? (
                    <>
                      <span className="isb-spin isb-spin--lt" />
                      &nbsp; Confirming…
                    </>
                  ) : (
                    "Confirm Slot →"
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <div style={styles.footer}>
        Powered by your HR platform · Slots refresh automatically
      </div>
    </div>
  );
}

const TopbarContent = () => (
  <>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="8" fill="#4f46e5" />
        <path
          d="M8 14l4 4 8-8"
          stroke="#fff"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <span
        style={{
          fontWeight: 700,
          fontSize: 16,
          color: "#1e1b4b",
          letterSpacing: "-0.3px",
        }}
      >
        Interview Scheduler
      </span>
    </div>
    <span
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: "#4f46e5",
        background: "#eef2ff",
        border: "1px solid #c7d2fe",
        borderRadius: 20,
        padding: "4px 12px",
      }}
    >
      🗓 Slot Booking
    </span>
  </>
);

// ─── Styles ───────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #f0f4ff 0%, #fafafa 60%, #f0fdf4 100%)",
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "0 16px 48px",
  },
  topbar: {
    width: "100%",
    maxWidth: 640,
    padding: "24px 0 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  card: {
    background: "#fff",
    borderRadius: 20,
    boxShadow:
      "0 4px 6px -1px rgba(0,0,0,0.07), 0 20px 40px -8px rgba(79,70,229,0.1)",
    width: "100%",
    maxWidth: 640,
    overflow: "hidden",
    border: "1px solid #e5e7eb",
  },
  cardHeader: {
    padding: "28px 32px 24px",
    background: "linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)",
  },
  title: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    color: "#fff",
    letterSpacing: "-0.4px",
  },
  subtitle: {
    margin: "6px 0 0",
    fontSize: 13.5,
    color: "rgba(255,255,255,0.8)",
    lineHeight: 1.5,
  },
  center: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "48px 32px",
  },
  errorBox: {
    margin: 28,
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 12,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    color: "#dc2626",
  },
  emptyBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "48px 32px",
    gap: 6,
    textAlign: "center",
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: 700,
    color: "#111827",
    margin: "8px 0 0",
  },
  emptySub: {
    fontSize: 14,
    color: "#6b7280",
    maxWidth: 320,
    lineHeight: 1.5,
    margin: 0,
  },
  groupsWrap: {
    padding: "24px 32px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 28,
  },
  dateGroup: { display: "flex", flexDirection: "column", gap: 12 },
  dateLabel: {
    fontSize: 12,
    fontWeight: 700,
    color: "#4f46e5",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  dateDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    background: "#4f46e5",
    display: "inline-block",
    flexShrink: 0,
  },
  slotsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 10,
  },
  slotBtn: {
    position: "relative",
    background: "#f9fafb",
    border: "1.5px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 16px",
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    gap: 2,
    textAlign: "left",
    outline: "none",
    transition: "all 0.15s ease",
  },
  slotBtnSelected: {
    background: "#eef2ff",
    borderColor: "#4f46e5",
    boxShadow: "0 0 0 3px rgba(79,70,229,0.15)",
  },
  slotTime: {
    fontSize: 15,
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.1,
  },
  slotSep: { fontSize: 11, color: "#9ca3af" },
  slotInterviewer: {
    fontSize: 11,
    color: "#6b7280",
    marginTop: 4,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  slotCheck: {
    position: "absolute",
    top: 8,
    right: 10,
    fontSize: 13,
    fontWeight: 800,
    color: "#4f46e5",
  },
  closingBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    marginTop: 12,
    background: "rgba(255,255,255,0.15)",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: 12.5,
    color: "rgba(255,255,255,0.95)",
    fontWeight: 500,
  },
  selectedBanner: {
    margin: "16px 32px 24px",
    background: "linear-gradient(135deg, #eef2ff, #e0e7ff)",
    border: "1.5px solid #c7d2fe",
    borderRadius: 14,
    padding: "16px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  selectedBannerTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: "#4f46e5",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    marginBottom: 3,
  },
  selectedBannerVal: { fontSize: 13.5, fontWeight: 600, color: "#1e1b4b" },
  confirmBtn: {
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "11px 22px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  },
  confirmBtnLoading: { background: "#6366f1", cursor: "not-allowed" },
  footer: {
    marginTop: 20,
    fontSize: 12,
    color: "#9ca3af",
    textAlign: "center",
  },
  successRing: {
    display: "flex",
    justifyContent: "center",
    paddingTop: 36,
    paddingBottom: 4,
  },
  successTitle: {
    textAlign: "center",
    fontSize: 22,
    fontWeight: 800,
    color: "#111827",
    margin: "0 0 8px",
  },
  successSub: {
    textAlign: "center",
    fontSize: 14,
    color: "#6b7280",
    margin: "0 32px 20px",
  },
  slotSummary: {
    margin: "0 32px 16px",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: 12,
    padding: "14px 18px",
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  slotSummaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    fontSize: 14,
  },
  slotSummaryLabel: { color: "#6b7280", fontWeight: 500 },
  slotSummaryVal: { color: "#111827", fontWeight: 700, textAlign: "right" },
  confirmNote: {
    textAlign: "center",
    fontSize: 13,
    color: "#9ca3af",
    margin: "0 32px 36px",
    lineHeight: 1.6,
  },
};

const cssRaw = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
.isb-slot:hover {
  border-color: #a5b4fc !important;
  background: #f5f3ff !important;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(79,70,229,0.12);
}
.isb-slot--selected {
  background: #eef2ff !important;
  border-color: #4f46e5 !important;
}
.isb-confirm-btn:hover {
  background: #4338ca !important;
}
@keyframes isb-spin-anim { to { transform: rotate(360deg); } }
.isb-spin {
  display: inline-block; width: 20px; height: 20px;
  border: 2.5px solid #e5e7eb; border-top-color: #4f46e5;
  border-radius: 50%; animation: isb-spin-anim 0.7s linear infinite;
  vertical-align: middle;
}
.isb-spin--lt {
  width: 14px; height: 14px;
  border-color: rgba(255,255,255,0.3); border-top-color: #fff;
}
`;
